import React, { lazy, useEffect, Suspense } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { SEOContext } from "gatsby-plugin-wpgraphql-seo";
import { CSSTransition } from "react-transition-group";
import { useLocation } from "@reach/router";

import useSwiperLazy from "src/hooks/useSwiperLazy";
import useOpenContactModal from "src/hooks/useOpenContactModal";
import useLandingReveal from "src/hooks/useLandingReveal";
import Header from "src/components/header";
import HeaderNew from "src/components/header-new";
import Footer from "src/components/footer";
import LandingLayoutHeader from "src/components/landing/LandingLayoutHeader";
import LandingLayoutFooter from "src/components/landing/LandingLayoutFooter";
import useVideoLinkTracking from "src/components/landing/shared/useVideoLinkTracking";
import { LandingVideoProvider } from "src/components/landing/shared/LandingVideoContext";
import {
  DEFAULT_MENU_ITEMS,
  DEFAULT_PRIMARY_BUTTON,
  DEFAULT_SECONDARY_BUTTON,
} from "src/components/landing/shared/nav";

const ContactModal = lazy(() => import("src/components/modal/contact-modal"));
import "../styles/global.css";
import "../styles/landing.css";
const duration = 300;

const transitionStyles = {
  entering: { left: 0 },
  entered: { left: 0 },
  exiting: { left: "100%" },
  exited: { left: "100%" },
};

const sendPageView = (pathname) => {
  if (typeof window?.gtag === "function") {
    window.gtag("event", "View Page", {
      event_category: "Category Page",
      event_label: `${pathname}`,
    });
  }
};
const isCustomLandingLayout = (settings) => {
  const variant = String(settings?.layoutVariant || "").toLowerCase();

  return Boolean(variant && variant !== "default");
};

// Static legal pages (Terms of Use, Privacy Policy) live under
// /aicustomblocks/ but have no WordPress data, so they need the landing
// chrome applied explicitly. The landing page itself ("/aicustomblocks/")
// is a WordPress page and is handled by the landingSettings branch above.
const AI_CUSTOM_BLOCKS_BASES = ["/aicustomblocks/", "/ai-custom-blocks/"];

const isAiCustomBlocksChild = (pathname) => {
  const normalized = String(pathname || "").replace(/\/+$/, "/");
  return AI_CUSTOM_BLOCKS_BASES.some(
    (base) => normalized.startsWith(base) && normalized !== base
  );
};

// On child pages, hash-only links (e.g. "#download") would resolve against the
// child URL and fail, so rewrite them to point back at the landing.
const toLandingUrl = (url) => {
  if (typeof url !== "string" || !url.startsWith("#")) return url;

  return `/aicustomblocks/${url}`;
};

const adaptLink = (link) =>
  link ? { ...link, url: toLandingUrl(link.url) } : link;

const adaptSettingsForChild = (settings) => {
  const base = settings || {
    menuItems: DEFAULT_MENU_ITEMS,
    primaryButton: DEFAULT_PRIMARY_BUTTON,
    secondaryButton: DEFAULT_SECONDARY_BUTTON,
  };

  const menuItems = Array.isArray(base.menuItems)
    ? base.menuItems.map((row) => {
        const item = row?.menuItem || row;
        if (!item) return row;
        const adapted = { ...item, url: toLandingUrl(item.url) };
        return row?.menuItem ? { ...row, menuItem: adapted } : adapted;
      })
    : base.menuItems;

  return {
    ...base,
    menuItems,
    primaryButton: adaptLink(base.primaryButton),
    secondaryButton: adaptLink(base.secondaryButton),
    updatesButton: base.updatesButton,
  };
};

const Index = ({ children, path, location, pageContext, data }) => {
  const { pathname } = useLocation();
  const routePathname = location?.pathname || pathname;
  const landingSettings =
    data?.wpPage?.landing_flexible?.landingSettings ||
    pageContext?.landingSettings ||
    null;
  const hasCustomLandingLayout = isCustomLandingLayout(landingSettings);
  const isLandingChild = isAiCustomBlocksChild(routePathname);
  const hasLegacyLandingHeader =
    routePathname.includes("on-the-future-edge") ||
    routePathname.includes("wptheme");

  useEffect(() => {
    sendPageView(pathname);
  }, [pathname]);

  useLandingReveal(hasCustomLandingLayout, routePathname);

  const [isOpen] = useOpenContactModal();
  const [_, loadSwiper] = useSwiperLazy();
  useEffect(() => {
    const scrollSetLoadSwiper = () => {
      loadSwiper(true);
      document.removeEventListener("scroll", scrollSetLoadSwiper);
    };
    setTimeout(() => {
      document.addEventListener("scroll", scrollSetLoadSwiper, false);
    }, 2000);
  }, []);
  const {
    wp: { seo },
    aiCustomBlocks,
    // allWpPage: { nodes: pages },
  } = useStaticQuery(graphql`
    query SiteInfoQuery {
      aiCustomBlocks: wpPage(slug: { eq: "aicustomblocks" }) {
        landing_flexible {
          landingSettings {
            layoutVariant
            headerLogo {
              altText
              mediaItemUrl
              sourceUrl
            }
            footerLogo {
              altText
              mediaItemUrl
              sourceUrl
            }
            menuItems {
              menuItem {
                target
                title
                url
              }
            }
            primaryButton {
              target
              title
              url
            }
            updatesButton {
              target
              title
              url
            }
            secondaryButton {
              target
              title
              url
            }
            copyrightText
            socialLinksRepeater {
              link
              icon {
                altText
                mediaItemUrl
                sourceUrl
              }
            }
          }
        }
      }
      wp {
        seo {
          contentTypes {
            post {
              title
              schemaType
              metaRobotsNoindex
              metaDesc
            }
            page {
              metaDesc
              metaRobotsNoindex
              schemaType
              title
            }
          }
          webmaster {
            googleVerify
            yandexVerify
            msVerify
            baiduVerify
          }
          schema {
            companyName
            personName
            companyOrPerson
            wordpressSiteName
            siteUrl
            siteName
            inLanguage
            logo {
              sourceUrl
              mediaItemUrl
              altText
            }
          }
          social {
            facebook {
              url
              defaultImage {
                sourceUrl
                mediaItemUrl
              }
            }
            instagram {
              url
            }
            linkedIn {
              url
            }
            mySpace {
              url
            }
            pinterest {
              url
              metaTag
            }
            twitter {
              username
            }
            wikipedia {
              url
            }
            youTube {
              url
            }
          }
        }
      }
      allWpPage {
        nodes {
          id
          slug
          title
        }
      }
    }
  `);
  const childLandingSettings = isLandingChild
    ? adaptSettingsForChild(
        aiCustomBlocks?.landing_flexible?.landingSettings || null
      )
    : null;
  const useLandingChrome = hasCustomLandingLayout || isLandingChild;
  const chromeSettings = isLandingChild ? childLandingSettings : landingSettings;

  // Track clicks on CMS-driven YouTube links by their parent <section id>.
  useVideoLinkTracking(useLandingChrome);

  const pageBody = (
    <>
      {useLandingChrome ? (
        <LandingLayoutHeader settings={chromeSettings} solid={isLandingChild} />
      ) : !hasLegacyLandingHeader ? (
        <Header path={path} />
      ) : (
        <HeaderNew />
      )}
      {children}
      <CSSTransition in={isOpen} timeout={duration} mountOnEnter key="modal">
        {(state) => (
          <div
            style={transitionStyles[state]}
            className="contact_modal_animation"
          >
            <Suspense>
              <ContactModal />{" "}
            </Suspense>
          </div>
        )}
      </CSSTransition>
      {useLandingChrome ? (
        <LandingLayoutFooter settings={chromeSettings} />
      ) : path !== "/wptheme/" && (
        <Footer isGatsbyLink={path !== "/on-the-future-edge/"} />
      )}
    </>
  );

  return (
    <>
      <SEOContext.Provider value={{ global: seo }}>
        {useLandingChrome ? (
          <LandingVideoProvider>{pageBody}</LandingVideoProvider>
        ) : (
          pageBody
        )}
      </SEOContext.Provider>
    </>
  );
};

export default Index;
